import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTranslation } from 'react-i18next';

interface Board {
  id: string;
  boardNumber: number;
  name: string;
}

interface Booking {
  id: string;
  boardId: string;
  userId: string;
  userName: string;
  startTime: Timestamp;
  endTime: Timestamp;
  status: 'confirmed' | 'cancelled' | 'completed';
}

interface BookingStatisticsProps {
  venueId: string;
  boards: Board[];
  pricePerHour: number;
}

type TimePeriod = 'daily' | 'weekly' | 'monthly';

const BookingStatistics: React.FC<BookingStatisticsProps> = ({ venueId, boards, pricePerHour }) => {
  const { t } = useTranslation();
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('daily');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBookings();
  }, [venueId, timePeriod]);

  const getDateRange = (): { start: Date; end: Date } => {
    const now = new Date();
    const start = new Date();
    const end = new Date();

    switch (timePeriod) {
      case 'daily':
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'weekly':
        const dayOfWeek = now.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday as first day
        start.setDate(now.getDate() - diff);
        start.setHours(0, 0, 0, 0);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      case 'monthly':
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end.setMonth(start.getMonth() + 1);
        end.setDate(0);
        end.setHours(23, 59, 59, 999);
        break;
    }

    return { start, end };
  };

  const loadBookings = async () => {
    if (!venueId) return;

    setLoading(true);
    try {
      const { start, end } = getDateRange();

      const q = query(
        collection(db, 'bookings'),
        where('venueId', '==', venueId),
        where('startTime', '>=', Timestamp.fromDate(start)),
        where('startTime', '<=', Timestamp.fromDate(end)),
        where('status', 'in', ['confirmed', 'completed'])
      );

      const querySnapshot = await getDocs(q);
      const bookingsData: Booking[] = [];

      // Process bookings and fetch missing userNames
      const bookingPromises = querySnapshot.docs.map(async (docSnapshot) => {
        const bookingData = { id: docSnapshot.id, ...docSnapshot.data() } as Booking;

        // If userName is missing, fetch it from users collection
        if (!bookingData.userName && bookingData.userId) {
          try {
            const userDoc = await getDoc(doc(db, 'users', bookingData.userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              bookingData.userName = userData.username || userData.displayName || userData.email || 'Ismeretlen felhasználó';
            } else {
              bookingData.userName = 'Ismeretlen felhasználó';
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
            bookingData.userName = 'Ismeretlen felhasználó';
          }
        }

        return bookingData;
      });

      const resolvedBookings = await Promise.all(bookingPromises);
      setBookings(resolvedBookings);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateHours = (booking: Booking): number => {
    const start = booking.startTime.toDate();
    const end = booking.endTime.toDate();
    return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  };

  const getStatsByBoard = () => {
    const stats: { [boardId: string]: { count: number; hours: number; revenue: number } } = {};

    boards.forEach(board => {
      stats[board.id] = { count: 0, hours: 0, revenue: 0 };
    });

    bookings.forEach(booking => {
      if (stats[booking.boardId]) {
        const hours = calculateHours(booking);
        stats[booking.boardId].count++;
        stats[booking.boardId].hours += hours;
        stats[booking.boardId].revenue += hours * pricePerHour;
      }
    });

    return stats;
  };

  const getTotalStats = () => {
    let totalCount = 0;
    let totalHours = 0;
    let totalRevenue = 0;

    bookings.forEach(booking => {
      const hours = calculateHours(booking);
      totalCount++;
      totalHours += hours;
      totalRevenue += hours * pricePerHour;
    });

    return { totalCount, totalHours, totalRevenue };
  };

  const calculateOccupancy = (): number => {
    const { start, end } = getDateRange();
    const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    const hoursPerDay = 12; // Average opening hours per day
    const totalPossibleHours = totalDays * hoursPerDay * boards.length;

    if (totalPossibleHours === 0) return 0;

    const { totalHours } = getTotalStats();
    return (totalHours / totalPossibleHours) * 100;
  };

  const calculateBoardOccupancy = (boardHours: number): number => {
    const { start, end } = getDateRange();
    const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    const hoursPerDay = 12; // Average opening hours per day
    const possibleHours = totalDays * hoursPerDay;

    if (possibleHours === 0) return 0;

    return (boardHours / possibleHours) * 100;
  };

  const getTopPlayers = () => {
    const playerStats: { [userId: string]: { name: string; hours: number; bookings: number } } = {};

    bookings.forEach(booking => {
      const hours = calculateHours(booking);

      if (!playerStats[booking.userId]) {
        playerStats[booking.userId] = {
          name: booking.userName,
          hours: 0,
          bookings: 0
        };
      }

      playerStats[booking.userId].hours += hours;
      playerStats[booking.userId].bookings++;
    });

    // Convert to array and sort by hours
    return Object.entries(playerStats)
      .map(([userId, stats]) => ({
        userId,
        ...stats
      }))
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 5);
  };

  const statsByBoard = getStatsByBoard();
  const topPlayers = getTopPlayers();
  const { totalCount, totalHours, totalRevenue } = getTotalStats();
  const occupancy = calculateOccupancy();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Period Selector */}
      <div className="card-dark p-4">
        <div className="flex gap-2">
          <button
            onClick={() => setTimePeriod('daily')}
            className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${timePeriod === 'daily'
              ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
              : 'bg-[#31383D] text-[#B8B8C3] hover:bg-[#3d4758]'
              }`}
          >
            {t('venueOperation.boards.daily')}
          </button>
          <button
            onClick={() => setTimePeriod('weekly')}
            className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${timePeriod === 'weekly'
              ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
              : 'bg-[#31383D] text-[#B8B8C3] hover:bg-[#3d4758]'
              }`}
          >
            {t('venueOperation.boards.weekly')}
          </button>
          <button
            onClick={() => setTimePeriod('monthly')}
            className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${timePeriod === 'monthly'
              ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
              : 'bg-[#31383D] text-[#B8B8C3] hover:bg-[#3d4758]'
              }`}
          >
            {t('venueOperation.boards.monthly')}
          </button>
        </div>
      </div>

      {/* Overall Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-dark p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-[#B8B8C3]">{t('venueOperation.boards.totalBookings')}</p>
              <p className="text-2xl font-bold text-white">{totalCount}</p>
              <p className="text-xs text-[#8a8f99]">{totalHours.toFixed(1)} {t('venueOperation.boards.hours')}</p>
            </div>
          </div>
        </div>

        <div className="card-dark p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-[#B8B8C3]">{t('venueOperation.boards.revenue')}</p>
              <p className="text-2xl font-bold text-green-400">{totalRevenue.toLocaleString()} Ft</p>
              <p className="text-xs text-[#8a8f99]">{pricePerHour.toLocaleString()} Ft/{t('venueOperation.boards.hours')}</p>
            </div>
          </div>
        </div>

        <div className="card-dark p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-[#B8B8C3]">{t('venueOperation.boards.occupancy')}</p>
              <p className="text-2xl font-bold text-purple-400">{occupancy.toFixed(1)}%</p>
              <p className="text-xs text-[#8a8f99]">{boards.length} {t('venueOperation.boards.boardsTab')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Per-Board Statistics */}
      <div className="card-dark p-6">
        <h3 className="text-xl font-bold text-white mb-4">📊 {t('venueOperation.boards.byBoard')}</h3>
        <div className="space-y-3">
          {boards.map(board => {
            const stats = statsByBoard[board.id];
            const boardOccupancy = calculateBoardOccupancy(stats.hours);
            return (
              <div key={board.id} className="bg-[#1d232a] p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="text-lg font-semibold text-white">{board.name}</h4>
                    <p className="text-sm text-[#B8B8C3]">#{board.boardNumber}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-cyan-400">{stats.count}</p>
                    <p className="text-xs text-[#8a8f99]">{t('venueOperation.boards.bookings')}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t border-[#31383D]">
                  <div>
                    <p className="text-xs text-[#B8B8C3] mb-1">{t('venueOperation.boards.hours')}</p>
                    <p className="text-lg font-semibold text-white">{stats.hours.toFixed(1)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#B8B8C3] mb-1">{t('venueOperation.boards.revenue')}</p>
                    <p className="text-lg font-semibold text-green-400">{stats.revenue.toLocaleString()} Ft</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#B8B8C3] mb-1">{t('venueOperation.boards.occupancy')}</p>
                    <p className="text-lg font-semibold text-purple-400">{boardOccupancy.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {boards.length === 0 && (
          <div className="text-center py-8">
            <p className="text-[#B8B8C3]">Nincs elérhető tábla</p>
          </div>
        )}
      </div>

      {/* Total Summary */}
      <div className="card-dark p-6 bg-gradient-to-r from-cyan-500/10 to-blue-600/10 border border-cyan-500/20">
        <h3 className="text-xl font-bold text-white mb-4">💰 {t('venueOperation.boards.total')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-sm text-[#B8B8C3] mb-1">{t('venueOperation.boards.bookings')}</p>
            <p className="text-3xl font-bold text-white">{totalCount}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-[#B8B8C3] mb-1">{t('venueOperation.boards.hours')}</p>
            <p className="text-3xl font-bold text-cyan-400">{totalHours.toFixed(1)}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-[#B8B8C3] mb-1">{t('venueOperation.boards.revenue')}</p>
            <p className="text-3xl font-bold text-green-400">{totalRevenue.toLocaleString()} Ft</p>
          </div>
        </div>
      </div>

      {/* Top Players */}
      {topPlayers.length > 0 && (
        <div className="card-dark p-6">
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-xl font-bold text-white">🏆 {t('venueOperation.boards.topPlayers')}</h3>
            <span className="text-sm text-[#8a8f99]">({t('venueOperation.boards.topPlayersDesc')})</span>
          </div>
          <div className="space-y-3">
            {topPlayers.map((player, index) => {
              const isTop3 = index < 3;
              const rankColors = ['text-yellow-400', 'text-gray-300', 'text-orange-400'];
              const rankIcons = ['🥇', '🥈', '🥉'];
              const bgColors = [
                'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500/30',
                'bg-gradient-to-r from-gray-400/20 to-gray-500/20 border-gray-400/30',
                'bg-gradient-to-r from-orange-500/20 to-red-500/20 border-orange-500/30'
              ];

              return (
                <div
                  key={player.userId}
                  className={`p-4 rounded-lg border transition-all ${isTop3
                    ? `${bgColors[index]} shadow-lg`
                    : 'bg-[#1d232a] border-[#31383D]'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`text-3xl font-bold ${isTop3 ? rankColors[index] : 'text-[#4a5568]'}`}>
                        {isTop3 ? rankIcons[index] : `#${index + 1}`}
                      </div>
                      <div>
                        <h4 className={`text-lg font-semibold ${isTop3 ? 'text-white' : 'text-[#B8B8C3]'}`}>
                          {player.name}
                        </h4>
                        <p className="text-sm text-[#8a8f99]">
                          {player.bookings} {t('venueOperation.boards.bookings')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${isTop3 ? rankColors[index] : 'text-cyan-400'}`}>
                        {player.hours.toFixed(1)}
                      </p>
                      <p className="text-xs text-[#8a8f99]">{t('venueOperation.boards.hours')}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {topPlayers.length === 0 && (
            <div className="text-center py-8">
              <p className="text-[#B8B8C3]">Nincs még foglalás ebben az időszakban</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingStatistics;
